import React, { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import { LoginModalProvider, useLoginModal } from "./components/LoginModalProvider";
import { BusinessAccountProvider } from "./business-dashboard/useBusinessAccount";

import Navbar from './components/Navbar';
import HeroTopSection from './components/HeroTopSection';
import ProFeed from './components/ProFeed';
import JiraniDeals from './components/JiraniDeals';
import PopularServices from './components/PopularServices';
import ForYourHousehold from './components/ForYourHousehold';
import TreatYourself from './components/TreatYourself';
import OnTheMove from './components/OnTheMove';
import Footer from './components/Footer';
import SearchResultsPage from './pages/SearchResultsPage';
import ProProfilePage from './pages/ProProfilePage';
import BookingsPage from './pages/BookingsPage';
import MessagingPage from './pages/MessagingPage';
import ProOnboardingPage from './pages/ProOnboardingPage';
import ProReviewDashboard from './pages/ProReviewDashboard';
import ReviewPage from './pages/ReviewPage';
import ManageServicesPage from './pages/ManageServicesPage';
import ServiceProductPage from "./pages/ServiceProductPage";
import JiraniProDashboard from './pages/JiraniProDashboard';
import ManageAvailabilityPage from './pages/ManageAvailabilityPage';
import JiraniEarningsDashboard from './pages/JiraniEarningsDashboard';
import ReviewsRatings from './pages/ReviewsRatings';
import SignUpPage from './pages/SignUpPage';
import NotificationsPage from './pages/NotificationsPage';
import HelpDisputePage from "./pages/HelpDisputePage";
import OrderTrackingPage from './pages/OrderTrackingPage';
import CustomerSettingsPage from './pages/CustomerSettingsPage';
import BookingDetailPage from './pages/BookingDetailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import PrivacyPage        from './pages/PrivacyPage';
import TermsPage          from './pages/TermsPage';
import ReportPage         from './pages/ReportPage';
import HowItWorksPage from './pages/HowItWorksPage';
import FindAJiraniProPage from './pages/FindAJiraniProPage';
import BrowseByLocationPage from './pages/BrowseByLocationPage';
import CustomerReviewsPage from './pages/CustomerReviewsPage';
import JiraniGuaranteePage from './pages/JiraniGuaranteePage';
import BecomeAProPage from './pages/BecomeAProPage';
import CommunityHubPage from './pages/CommunityHubPage';
import TipsAndResourcesPage from "./pages/TipsAndResourcesPage";
import EventsPage from "./pages/EventsPage";
import JiraniBusinessPage from "./pages/JiraniBusinessPage";
import PropertyManagementPage from "./pages/PropertyManagementPage";
import CorporateServicesPage from "./pages/CorporateServicesPage";
import PartnerWithUsPage from "./pages/PartnerWithUs";
import ContactSalesPage from "./pages/ContactSalesPage";
import AboutPage from "./pages/AboutPage";
import CareersPage from "./pages/CareersPage";
import TrustandSafetyPage from "./pages/TrustandSafetyPage";
import PressAndNewsPage from './pages/PressAndNewsPage';
import CookiePolicyPage from "./pages/CookiePolicyPage";
import PartnershipsPage from './pages/PartnershipsPage';
import InvestorRelationsPage from "./pages/InvestorRelationsPage";
import AffiliatesPage from "./pages/AffiliatesPage";
import GuidesIndexPage from "./pages/GuidesIndexPage";
import GuideArticlePage  from "./pages/GuideArticlePage";
import CleaningCategoryPage from "./pages/CleaningCategoryPage";
import PlumbingCategoryPage from "./pages/PlumbingCategoryPage";
import ElectricalCategoryPage from "./pages/ElectricalCategoryPage";
import ElectronicsRepairCategoryPage from "./pages/ElectronicsRepairCategoryPage";
import HairBeautyPage from "./pages/HairBeauty";
import HomeTutoringPage from "./pages/HomeTutoring";
import PhotographyCategoryPage from "./pages/PhotographyCategoryPage";
import GardeningLandscapingPage from './pages/GardeningLandscapingPage';
import CarpentryFurniturePage from './pages/CarpentryFurniturePage';
import MovingRelocationPage from './pages/MovingRelocationPage';
import BabysittingChildcarePage from './pages/BabysittingChildcarePage';
import FitnessTrainingPage from "./pages/FitnessTrainingPage";
import AutoRepairPage from "./pages/AutoRepairPage";
import ErrandsDeliveryPage from "./pages/ErrandsDeliveryPage";
import BusinessSignUpPage from "./pages/BusinessSignUpPage";
import BusinessDashboard from "./business-dashboard/BusinessDashboard";
import ProFeedPage from "./pages/ProFeedPage";
import JiraniProLandingPage from "./pages/JiraniProLandingPage";
import DevLoginPage from "./pages/DevLoginPage";
import ProApplicationRejectedPage from "./pages/ProApplicationRejectedPage";
import JiraniChefCategoryPage from './pages/JiraniChefCategoryPage';

import AdminLayout from './admin/AdminLayout';
import AdminOverview from './admin/AdminOverview';
import AdminPros from './admin/AdminPros';
import AdminProSignups from './admin/AdminProSignups';
import AdminCustomers from './admin/AdminCustomers';
import AdminJobs from './admin/AdminJobs';
import AdminCategories from './admin/AdminCategories';
import AdminContent from './admin/AdminContent';
import AdminMedia from './admin/AdminMedia';
import AdminPayments from './admin/AdminPayments';
import AdminMessages from './admin/AdminMessages';
import { AdminNotifications, AdminSettings, AdminLogs } from './admin/AdminStubs';

import './App.css';

// NOTE ON /login:
// There is no longer a standalone /login route or LoginPage.jsx page.
// Signing in now happens exclusively through LoginModal, a popup rendered
// once by LoginModalProvider (wrapped around the whole app below) and
// triggered from anywhere via the useLoginModal() hook. This replaces the
// old openAuth(mode) navigate("/login") pattern entirely — openAuth is no
// longer threaded through props; components call useLoginModal().open()
// directly instead. /signup remains a full page, unchanged.

// NOTE ON /dev-login:
// Dev/test-only route that signs in with email + password via
// supabase.auth.signInWithPassword(), bypassing the phone-OTP flow
// entirely. Only works for accounts that have had a password manually
// set on auth.users (see the SQL scripts used to set up test pro and
// test client accounts). Remove this route before shipping to production.

// NOTE ON /business/account:
// Wrapped with BusinessAccountProvider so every section inside the dashboard
// (Overview, Team, Bookings, Billing, Reports, Members, Settings) can read
// account type, plan limits, subscription status, and user role from context
// via useBusinessAccount() without prop-drilling or duplicate Supabase fetches.
//
// NOTE ON /pro/:id:
// Also wrapped with BusinessAccountProvider so ProProfilePage can read the
// current business context and render the correct CTA:
//   - "Reserve this Pro"  → pro not on team and slots remain
//   - "Book on-demand"    → quota reached or pro already reserved
//   - "Message"           → pro is already on the team

// NOTE ON /pro-signup:
// JiraniProLandingPage is the premium seller acquisition page targeting
// skilled Kenyan workers who want to join JiraniApp as pros. It lives at
// /pro-signup so it coexists alongside /become-a-pro without breaking
// existing links. Form submissions write to the pro_signups Supabase table
// (name, phone, trade, area, created_at). Place the file at:
// src/pages/JiraniProLandingPage.jsx

function HomePage() {
  const [navQuery, setNavQuery] = useState("");
  const [showNavSearch, setShowNavSearch] = useState(false);
  const { open: openLogin } = useLoginModal();

  return (
    <div className="font-sans">
      <HeroTopSection
        onShowNavSearchChange={setShowNavSearch}
        navQuery={navQuery}
        setNavQuery={setNavQuery}
        openAuth={openLogin}
      />
      <ProFeed />
      <JiraniDeals />
      <PopularServices />
      <ForYourHousehold />
      <TreatYourself />
      <OnTheMove />
      <Footer />
    </div>
  );
}

function SearchPage() {
  const { open: openLogin } = useLoginModal();
  return (
    <div style={{ minHeight: "100vh" }}>
      <Navbar showNavSearch={false} navQuery="" setNavQuery={() => {}} openAuth={openLogin} />
      <SearchResultsPage />
    </div>
  );
}

function ProPage() {
  const { open: openLogin } = useLoginModal();
  return (
    <BusinessAccountProvider>
      <ProProfilePage openAuth={openLogin} />
    </BusinessAccountProvider>
  );
}

function AppRoutes() {
  return (
    <>
      <Routes>
        <Route path="/" element={<JiraniProLandingPage />} />
        <Route path="/home" element={<HomePage />} />
        <Route path="/search"                   element={<SearchPage />} />
        <Route path="/profeed"                  element={<ProFeedPage />} />
        <Route path="/pro/:id"                  element={<ProPage />} />
        <Route path="/bookings"                 element={<BookingsPage />} />
        <Route path="/bookings/:id"             element={<BookingDetailPage />} />
        <Route path="/bookings/:id/track"       element={<OrderTrackingPage />} />
        <Route path="/messages"                 element={<MessagingPage />} />
        <Route path="/messages/:conversationId" element={<MessagingPage />} />
        <Route path="/pro-onboarding"           element={<ProOnboardingPage />} />
        <Route path="/pro-dashboard"            element={<Navigate to="/pro/dashboard" replace />} />
        <Route path="/manage-services"          element={<ManageServicesPage />} />
        <Route path="/account"                  element={<CustomerSettingsPage />} />
        <Route path="/forgot-password"          element={<ForgotPasswordPage />} />
        <Route path="/dev-login"                element={<DevLoginPage />} />
        <Route path="/terms"                    element={<TermsPage />} />
        <Route path="/privacy"                  element={<PrivacyPage />} />
        <Route path="/report/:proId"            element={<ReportPage />} />
        <Route path="/how-it-works"             element={<HowItWorksPage />} />
        <Route path="/find-a-jiranipro"         element={<FindAJiraniProPage />} />
        <Route path="/browse-by-location"       element={<BrowseByLocationPage />} />
        <Route path="/customer-reviews"         element={<CustomerReviewsPage />} />
        <Route path="/guarantee"                element={<JiraniGuaranteePage />} />
        <Route path="/become-a-pro"             element={<BecomeAProPage />} />
        <Route path="/pro-signup"               element={<JiraniProLandingPage />} />
        <Route path="/community-hub"            element={<CommunityHubPage />} />
        <Route path="/tips"                     element={<TipsAndResourcesPage />} />
        <Route path="/events"                   element={<EventsPage />} />
        <Route path="/business"                 element={<JiraniBusinessPage />} />
        <Route path="/pro-signup/rejected" element={<ProApplicationRejectedPage />} />
        <Route path="/services/chef" element={<JiraniChefCategoryPage />} />

        {/* Business Dashboard — wrapped with BusinessAccountProvider so all
            nested sections share one account/plan/role context */}
        <Route
          path="/business/account"
          element={
            <BusinessAccountProvider>
              <BusinessDashboard />
            </BusinessAccountProvider>
          }
        />

        <Route path="/property-management"      element={<PropertyManagementPage />} />
        <Route path="/corporate"                element={<CorporateServicesPage />} />
        <Route path="/partner"                  element={<PartnerWithUsPage />} />
        <Route path="/contact-sales"            element={<ContactSalesPage />} />
        <Route path="/about"                    element={<AboutPage />} />
        <Route path="/careers"                  element={<CareersPage />} />
        <Route path="/trust-and-safety"         element={<TrustandSafetyPage />} />
        <Route path="/news"                     element={<PressAndNewsPage />} />
        <Route path="/cookies"                  element={<CookiePolicyPage />} />
        <Route path="/partnerships"             element={<PartnershipsPage />} />
        <Route path="/investors"                element={<InvestorRelationsPage />} />
        <Route path="/affiliates"               element={<AffiliatesPage />} />
        <Route path="/guides"                   element={<GuidesIndexPage />} />
        <Route path="/services/cleaning"        element={<CleaningCategoryPage />} />
        <Route path="/services/plumbing"        element={<PlumbingCategoryPage />} />
        <Route path="/services/electrical"      element={<ElectricalCategoryPage />} />
        <Route path="/services/electronics-repair" element={<ElectronicsRepairCategoryPage />} />
        <Route path="/services/hair-beauty"     element={<HairBeautyPage />} />
        <Route path="/services/home-tutoring"   element={<HomeTutoringPage />} />
        <Route path="/services/photography"     element={<PhotographyCategoryPage />} />
        <Route path="/services/gardening"       element={<GardeningLandscapingPage />} />
        <Route path="/services/carpentry-furniture" element={<CarpentryFurniturePage />} />
        <Route path="/services/moving"          element={<MovingRelocationPage />} />
        <Route path="/services/childcare"       element={<BabysittingChildcarePage />} />
        <Route path="/services/fitness"         element={<FitnessTrainingPage />} />
        <Route path="/services/auto-repair"     element={<AutoRepairPage />} />
        <Route path="/services/errands-delivery" element={<ErrandsDeliveryPage />} />
        <Route path="/signup/business"          element={<BusinessSignUpPage />} />
        <Route path="/service/:id" element={<ServiceProductPage />} />

        {/* Pro Dashboard */}
        <Route path="/pro/dashboard"            element={<JiraniProDashboard />} />
        <Route path="/pro/availability"         element={<ManageAvailabilityPage />} />
        <Route path="/pro/payouts"              element={<JiraniEarningsDashboard />} />
        <Route path="/pro/support"              element={<HelpDisputePage />} />

        {/* Jobs — reuse BookingsPage for pro job list */}
        <Route path="/jobs"                     element={<BookingsPage />} />

        <Route path="/earnings"                 element={<Navigate to="/pro/payouts" replace />} />
        <Route path="/pro-review-dashboard"     element={<ProReviewDashboard />} />
        <Route path="/review/:bookingId"        element={<ReviewPage />} />
        <Route path="/reviews"                  element={<ReviewsRatings />} />
        <Route path="/signup"                   element={<SignUpPage />} />
        <Route path="/notifications"            element={<NotificationsPage />} />
        <Route path="/help"                     element={<HelpDisputePage />} />
        <Route path="/guides/:id"               element={<GuideArticlePage />} />

        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<Navigate to="/admin/overview" replace />} />
          <Route path="overview"      element={<AdminOverview />} />
          <Route path="pros"          element={<AdminPros />} />
          <Route path="signups"       element={<AdminProSignups />} />
          <Route path="customers"     element={<AdminCustomers />} />
          <Route path="jobs"          element={<AdminJobs />} />
          <Route path="categories"    element={<AdminCategories />} />
          <Route path="payments"      element={<AdminPayments />} />
          <Route path="content"       element={<AdminContent />} />
          <Route path="media"         element={<AdminMedia />} />
          <Route path="messages"      element={<AdminMessages />} />
          <Route path="notifications" element={<AdminNotifications />} />
          <Route path="settings"      element={<AdminSettings />} />
          <Route path="logs"          element={<AdminLogs />} />
        </Route>
      </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LoginModalProvider>
        <AppRoutes />
      </LoginModalProvider>
    </BrowserRouter>
  );
}

export default App;